import { useState } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "./Reveal";
import { api, scrollToId } from "../lib/api";

export default function BrandSearch() {
  const [brand, setBrand] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const b = brand.trim();
    if (!b) return;
    try {
      await api.post("/brand-searches", { brand: b });
    } catch {
      // logging is best-effort; the visitor experience continues regardless
    }
    setDone(b);
  };

  return (
    <section data-testid="brand-search-section" className="border-y border-border bg-secondary/40 py-16 md:py-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
          <div className="lg:max-w-md shrink-0">
            <h2 className="font-serif text-primary tracking-tight text-2xl sm:text-3xl leading-snug" data-testid="brand-search-heading">
              Own with Marriott, Hilton, Wyndham, Disney — or another brand?
            </h2>
            <p className="mt-3 text-sm text-foreground/60">
              Tell us your brand and we'll point you at the guidance written for your situation.
            </p>
          </div>
          {!done ? (
            <form onSubmit={submit} className="flex-1 flex flex-col sm:flex-row gap-3" data-testid="brand-search-form">
              <div className="relative flex-1">
                <MagnifyingGlass size={18} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/35" />
                <input
                  data-testid="brand-search-input"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="Type your ownership brand…"
                  className="w-full bg-card border border-border rounded-sm pl-11 pr-4 py-4 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300"
                />
              </div>
              <button
                data-testid="brand-search-submit"
                type="submit"
                className="group flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-4 rounded-full font-semibold text-sm hover:bg-accent transition-colors duration-300 shrink-0"
              >
                Find our guidance
                <ArrowRight size={15} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-1 bg-card border border-border rounded-sm px-7 py-6"
              data-testid="brand-search-result"
            >
              <p className="font-serif text-xl text-primary">
                <em className="text-accent">{done}</em> owners are among the most common enquiries we receive.
              </p>
              <p className="mt-2 text-sm text-foreground/60">
                Whatever the brand, the maths and the red flags are the same.
              </p>
              <div className="mt-5 flex flex-wrap gap-4">
                <button
                  data-testid="brand-search-cta-quiz"
                  onClick={() => scrollToId("decision-tool")}
                  className="text-sm font-semibold text-primary border-b-2 border-accent pb-0.5 hover:text-accent transition-colors duration-300"
                >
                  Take the decision tool
                </button>
                <button
                  data-testid="brand-search-cta-consult"
                  onClick={() => scrollToId("consult")}
                  className="text-sm font-semibold text-primary border-b-2 border-accent pb-0.5 hover:text-accent transition-colors duration-300"
                >
                  Request a consultation
                </button>
                <button
                  data-testid="brand-search-again"
                  onClick={() => { setDone(null); setBrand(""); }}
                  className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
                >
                  Search another brand
                </button>
              </div>
            </motion.div>
          )}
        </Reveal>
      </div>
    </section>
  );
}
